import { BadgeWithIcon } from '@orn/shared-web/components/Badge';
import Button from '@orn/shared-web/components/Button/Button';
import { CheckIcon, PlusIcon } from '@orn/shared-web/icons';
import * as React from 'react';

import onboardingInstitutionsIcons from '../../../assets/onboarding-institutions-icons.png';
import plaidLogo from '../../../assets/plaid-logo.svg';
import { useFinancialAccounts } from '../../../hooks/queries/useFinancialAccounts';
import { groupAccountsBySubtype, subtypeToDisplayName, supportedSubtypes } from '../../../utils/accounts';
import AccountMetadata from '../../AccountMetadata/AccountMetadata';
import Entity from '../../entity';
import PlaidLinkButton from '../../plaid/PlaidLinkButton/PlaidLinkButton';
import WizardContainer from '../WizardContainer';
import WizardTitle from '../WizardTitle';

import type { IWizardStepProps } from './types';
import type { FinancialAccount } from '../../../hooks/queries/useFinancialAccounts';

const NoFinancialAccountsContent = () => {
  const title = (
    <span className="w-100 block text-center">
      Connect your accounts securely with
      <img src={plaidLogo} alt="Plaid logo" className="ml-3 inline h-8 align-middle" />
    </span>
  );

  return (
    <>
      <WizardTitle
        title={title}
        subtext="Read-only access to balances and transactions. Bank level security trusted by millions."
      />

      {/* Placeholder for grid of apps */}
      <div className="flex flex-col gap-4 pb-4">
        <div className="border-primary/20 flex min-h-[200px] w-full items-center justify-center rounded-lg border border-dashed">
          <img src={onboardingInstitutionsIcons} alt="Onboarding institutions icons" />
        </div>
      </div>

      {/* Actions */}
      <div className="flex w-full flex-col gap-2">
        <PlaidLinkButton bankConnectionId={null} size="xl" className="w-full">
          Connect my accounts
        </PlaidLinkButton>
      </div>
    </>
  );
};

const FinancialAccountsContent = ({
  financialAccounts,
  onContinue,
}: {
  financialAccounts: FinancialAccount[];
  onContinue?: () => void;
}) => {
  const groupedAccounts = React.useMemo(() => groupAccountsBySubtype(financialAccounts), [financialAccounts]);

  const isContinueDisabled = React.useMemo(() => {
    return financialAccounts.length === 0;
  }, [financialAccounts]);

  return (
    <>
      <WizardTitle title="Connect your accounts" />

      {/* Connected Accounts */}
      <div className="flex w-full flex-col gap-4">
        <div className="scrollbar-visible flex max-h-[calc(100vh-450px)] w-full flex-col gap-5 overflow-y-auto">
          {supportedSubtypes.map((subtype) => {
            const accounts = groupedAccounts[subtype];
            if (!accounts || accounts.length === 0) {
              return null;
            }

            return (
              <div key={subtype} className="flex flex-col gap-2">
                <div className="text-primary text-md font-semibold">{subtypeToDisplayName(subtype)}</div>
                {accounts.map((account) => (
                  <div
                    key={account.id}
                    className="bg-primary border-secondary flex w-full items-center justify-between gap-4 rounded-xl border p-3"
                  >
                    <div className="flex min-w-0 flex-1 items-center gap-4">
                      <Entity.Avatar entity={account} size="lg" />
                      <div className="flex min-w-0 flex-1 flex-col gap-0">
                        <div className="text-primary truncate text-lg font-semibold">{account.name}</div>
                        <AccountMetadata
                          className="text-sm"
                          subtype={account.subtype}
                          bankName={account.bank.name}
                          mask={account.mask}
                        />
                      </div>
                    </div>
                    <BadgeWithIcon color="success" size="sm" type="color" iconLeading={CheckIcon}>
                      Connected
                    </BadgeWithIcon>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
        <div className="flex w-full items-center justify-center">
          <PlaidLinkButton bankConnectionId={null} size="xl" color="primary" className="w-full" iconLeading={PlusIcon}>
            Add another account
          </PlaidLinkButton>
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-col items-center pt-2">
        <Button onClick={onContinue} disabled={isContinueDisabled} size="lg" color="link-gray" className="w-full">
          Finished adding accounts
        </Button>
      </div>
    </>
  );
};

interface IBaseConnectBankStepProps extends IWizardStepProps {
  financialAccounts: FinancialAccount[];
  isLoading?: boolean;
  onContinue?: () => void;
}

export const BaseConnectBankStep = ({ onNext, onBack, financialAccounts, isLoading }: IBaseConnectBankStepProps) => {
  const hasAccounts = financialAccounts.length > 0 && !isLoading;

  return (
    <WizardContainer step={3} onBack={onBack}>
      {/* Content */}
      <div className="flex w-full flex-col items-center gap-2">
        {hasAccounts && <FinancialAccountsContent financialAccounts={financialAccounts} onContinue={onNext} />}
        {!hasAccounts && <NoFinancialAccountsContent />}
      </div>
    </WizardContainer>
  );
};

const ConnectBankStep = (props: IWizardStepProps) => {
  const { financialAccounts, loading } = useFinancialAccounts();

  return <BaseConnectBankStep financialAccounts={financialAccounts} isLoading={loading} {...props} />;
};

export default ConnectBankStep;
